import {
  Account,
  AccountLoginLanding,
  AccountLoginLandingUpdateInput,
  MemberAutoAnchorUploadsUpdateInput,
  MemberNeoDefaultInputModeUpdateInput,
  MemberObservationNewThresholdsUpdateInput,
  MemberLandingViewUpdateInput,
  AccountNameAvailabilityInput,
  AccountNameAvailabilityResult,
  AccountPreferencesUpdateInput,
  LoginLandingViewOption,
  InviteAcceptInput,
  InviteAcceptResult,
  InviteCheckInput,
  InviteCheckResult,
  LoginInput,
  LoginResult,
  LogoutInput,
  LogoutResult,
  MfaLoginResult,
  MfaPreferenceInput,
  MfaToggleInput,
  MfaVerifyEmailInput,
  MfaVerifyPasskeyInput,
  PasskeyRegisterVerifyInput,
  PasskeyRegisterVerifyResult,
  PasswordResetInput,
  PasswordResetResult,
  PasswordResetSendEmailInput,
  PlanOffer,
  RefreshTokenInput,
  RefreshTokenResult,
  SignupCheckoutResult,
  SignupCompleteInput,
  SignupEmailCodeSendInput,
  SignupEmailCodeSendResult,
  SignupEmailCodeVerifyInput,
  SignupEmailCodeVerifyResult,
  SignupInput,
  UserInfoResult,
} from '@vivport/backend/types';
import { MessageResult } from '@vivport/backend/types/common';
import { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { httpClient } from './http-client';

/**
 * Authentication API
 * Type-safe wrappers for all authentication endpoints
 */

// ============================================================================
// Core Authentication
// ============================================================================

/**
 * Login with email and password
 */
export async function login(input: LoginInput): Promise<LoginResult | MfaLoginResult> {
  return httpClient.post<LoginResult | MfaLoginResult, LoginInput>('/auth/login', input);
}

/**
 * Start payment-first signup: returns a Stripe checkout URL, or a completed
 * login when the email has a 100% discount
 */
export async function signupCheckout(input: SignupInput): Promise<SignupCheckoutResult> {
  return httpClient.post<SignupCheckoutResult, SignupInput>('/auth/signup/checkout', input);
}

/**
 * Complete signup after returning from Stripe checkout
 */
export async function signupComplete(input: SignupCompleteInput): Promise<LoginResult> {
  return httpClient.post<LoginResult, SignupCompleteInput>('/auth/signup/complete', input);
}

/**
 * Send a signup email verification code
 */
export async function sendSignupEmailCode(
  input: SignupEmailCodeSendInput
): Promise<SignupEmailCodeSendResult> {
  return httpClient.post<SignupEmailCodeSendResult, SignupEmailCodeSendInput>(
    '/auth/signup/email-code/send',
    input
  );
}

/**
 * Verify a signup email verification code
 */
export async function verifySignupEmailCode(
  input: SignupEmailCodeVerifyInput
): Promise<SignupEmailCodeVerifyResult> {
  return httpClient.post<SignupEmailCodeVerifyResult, SignupEmailCodeVerifyInput>(
    '/auth/signup/email-code/verify',
    input
  );
}

/**
 * Public plan offer for the signup page (discount resolved by email when given)
 */
export async function getSignupPlanOffer(email?: string): Promise<PlanOffer> {
  const query = email ? `?email=${encodeURIComponent(email)}` : '';
  return httpClient.get<PlanOffer>(`/auth/signup/plan-offer${query}`);
}

/**
 * Check whether an account name is available, with suggestions when taken
 */
export async function checkAccountNameAvailability(
  input: AccountNameAvailabilityInput
): Promise<AccountNameAvailabilityResult> {
  return httpClient.post<AccountNameAvailabilityResult, AccountNameAvailabilityInput>(
    '/auth/account-name/availability',
    input
  );
}

/**
 * Refresh access token
 */
export async function refreshToken(input: RefreshTokenInput): Promise<RefreshTokenResult> {
  return httpClient.post<RefreshTokenResult, RefreshTokenInput>('/auth/refresh', input);
}

/**
 * Logout user
 */
export async function logout(input: LogoutInput): Promise<LogoutResult> {
  return httpClient.post<LogoutResult, LogoutInput>('/auth/logout', input);
}

/**
 * Mark the Neo first-login introduction as seen for the current account
 * @requires Authentication
 */
export async function markNeoIntroSeen(): Promise<void> {
  await httpClient.post<void>('/protected/account/neo-intro-seen');
}

/**
 * Update account-level display preferences
 * @requires Authentication
 */
export async function updateAccountPreferences(input: AccountPreferencesUpdateInput): Promise<Account> {
  return httpClient.patch<Account, AccountPreferencesUpdateInput>('/protected/account/preferences', input);
}

/**
 * Get the account's login landing preference
 * @requires Authentication
 */
export async function getLoginLanding(): Promise<AccountLoginLanding> {
  return httpClient.get<AccountLoginLanding>('/protected/account/login-landing');
}

/**
 * Update the account's login landing preference
 * @requires Authentication
 */
export async function updateLoginLanding(input: AccountLoginLandingUpdateInput): Promise<AccountLoginLanding> {
  return httpClient.patch<AccountLoginLanding, AccountLoginLandingUpdateInput>('/protected/account/login-landing', input);
}

/**
 * List the landing-view options (tabs + visible saved filters) for one of the account's members
 * @requires Authentication
 */
export async function getLoginLandingOptions(memberId: number): Promise<{ items: LoginLandingViewOption[] }> {
  return httpClient.get<{ items: LoginLandingViewOption[] }>(`/protected/account/login-landing/options?memberId=${memberId}`);
}

/**
 * Set a member's landing view (opened on login or member switch)
 * @requires Authentication
 */
export async function updateMemberLandingView(
  memberId: number,
  input: MemberLandingViewUpdateInput
): Promise<AccountLoginLanding> {
  return httpClient.patch<AccountLoginLanding, MemberLandingViewUpdateInput>(
    `/protected/account/members/${memberId}/landing-view`,
    input
  );
}

/**
 * Set whether a member's uploads are filed automatically by the platform
 * @requires Authentication
 */
export async function updateMemberAutoAnchorUploads(
  memberId: number,
  input: MemberAutoAnchorUploadsUpdateInput
): Promise<AccountLoginLanding> {
  return httpClient.patch<AccountLoginLanding, MemberAutoAnchorUploadsUpdateInput>(
    `/protected/account/members/${memberId}/auto-anchor-uploads`,
    input
  );
}

/**
 * Set which input mode a member's Neo chat opens in from the Speak2Me button
 * @requires Authentication
 */
export async function updateMemberNeoDefaultInputMode(
  memberId: number,
  input: MemberNeoDefaultInputModeUpdateInput
): Promise<AccountLoginLanding> {
  return httpClient.patch<AccountLoginLanding, MemberNeoDefaultInputModeUpdateInput>(
    `/protected/account/members/${memberId}/neo-default-input-mode`,
    input
  );
}

/**
 * Set when Observation Capture defaults to a new Observation for a member
 * @requires Authentication
 */
export async function updateMemberObservationNewThresholds(
  memberId: number,
  input: MemberObservationNewThresholdsUpdateInput
): Promise<AccountLoginLanding> {
  return httpClient.patch<AccountLoginLanding, MemberObservationNewThresholdsUpdateInput>(
    `/protected/account/members/${memberId}/observation-new-thresholds`,
    input
  );
}

/**
 * Get current user information
 * @requires Authentication
 */
export async function getUser(): Promise<UserInfoResult> {
  return httpClient.get<UserInfoResult>('/auth/user');
}

// ============================================================================
// Multi-Factor Authentication (MFA)
// ============================================================================

/**
 * Verify email MFA code
 * @requires MFA token
 */
export async function verifyEmailMfa(input: MfaVerifyEmailInput): Promise<LoginResult> {
  return httpClient.post<LoginResult, MfaVerifyEmailInput>('/auth/mfa/verify-email', input);
}

/**
 * Send email MFA verification code
 * @requires MFA token
 */
export async function sendEmailMfaCode(mfaToken: string): Promise<MessageResult> {
  return httpClient.post<MessageResult>('/auth/mfa/send-email-code', { mfaToken });
}

/**
 * Generate passkey challenge for MFA
 * @requires MFA token
 */
export async function getPasskeyMfaChallenge(mfaToken: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
  return httpClient.post<PublicKeyCredentialRequestOptionsJSON>('/auth/mfa/passkey-challenge', { mfaToken });
}

/**
 * Verify passkey MFA
 * @requires MFA token
 */
export async function verifyPasskeyMfa(input: MfaVerifyPasskeyInput): Promise<LoginResult> {
  return httpClient.post<LoginResult, MfaVerifyPasskeyInput>('/auth/mfa/verify-passkey', input);
}

/**
 * Update MFA method preference
 * @requires Authentication
 */
export async function updateMfaPreference(input: MfaPreferenceInput): Promise<MessageResult> {
  return httpClient.put<MessageResult, MfaPreferenceInput>('/auth/mfa/preference', input);
}

/**
 * Toggle MFA on/off
 * @requires Authentication
 */
export async function toggleMfa(input: MfaToggleInput): Promise<MessageResult> {
  return httpClient.put<MessageResult, MfaToggleInput>('/auth/mfa/toggle', input);
}

// ============================================================================
// Passkey Registration
// ============================================================================

/**
 * Get passkey registration options
 * @requires Authentication
 */
export async function getPasskeyRegistrationOptions(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  return httpClient.post<PublicKeyCredentialCreationOptionsJSON>('/auth/passkey/register-options');
}

/**
 * Verify passkey registration
 * @requires Authentication
 */
export async function verifyPasskeyRegistration(
  input: PasskeyRegisterVerifyInput
): Promise<PasskeyRegisterVerifyResult> {
  return httpClient.post<PasskeyRegisterVerifyResult, PasskeyRegisterVerifyInput>(
    '/auth/passkey/register-verify',
    input
  );
}

// ============================================================================
// Password Reset
// ============================================================================

/**
 * Send password reset email
 */
export async function sendPasswordResetEmail(input: PasswordResetSendEmailInput): Promise<MessageResult> {
  return httpClient.post<MessageResult, PasswordResetSendEmailInput>('/auth/password-reset/send-email', input);
}

/**
 * Reset password with token
 */
export async function resetPassword(input: PasswordResetInput): Promise<PasswordResetResult> {
  return httpClient.post<PasswordResetResult, PasswordResetInput>('/auth/password-reset/reset', input);
}

// ============================================================================
// Invitations
// ============================================================================

/**
 * Check invitation status
 */
export async function checkInviteStatus(input: InviteCheckInput): Promise<InviteCheckResult> {
  return httpClient.post<InviteCheckResult, InviteCheckInput>('/auth/check-invite-status', input);
}

/**
 * Accept invitation
 */
export async function acceptInvite(input: InviteAcceptInput): Promise<InviteAcceptResult> {
  return httpClient.post<InviteAcceptResult, InviteAcceptInput>('/auth/accept-invite', input);
}

